import {
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CircleCheck } from 'common/assets/images/circle-check.svg';
import { ReactComponent as CircleDeleteCheck } from 'common/assets/images/circle-delete-fill.svg';
import { ReactComponent as FolderOpenAngledIcon } from 'common/assets/images/folder-open-angled.svg';
import { ReactComponent as MoreMenu } from 'common/assets/images/more-menu.svg';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { RootState } from 'common/store/AppStore';
import {
	setHighlightButtonText,
	setParserEndHighlightId,
	setParserHighlights,
} from 'common/store/reducer/commonReducer';

import type { IHighlight } from 'react-pdf-highlighter';

import styles from './SelectSection.module.scss';

interface ISelectSectionProps {
	textArray: IHighlight[];
}

const SelectSection = (props: ISelectSectionProps) => {
	const { textArray } = props;
	const [highlightedTextArray, setHighlightedTextArray] =
		useState<IHighlight[]>(textArray);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedHighlightedText, setSelectedHighlightedText] =
		useState<any>('');
	const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false);

	const dispatch = useDispatch();

	const parserHighlights = useSelector(
		(state: RootState) => state.common.parserHighlights,
	);

	const endHighlightId = useSelector(
		(state: RootState) => state.common.parserEndHighlightId,
	);

	useEffect(() => {
		setHighlightedTextArray(parserHighlights);
	}, [parserHighlights]);

	useEffect(() => {
		dispatch(setParserHighlights(highlightedTextArray));
	}, [highlightedTextArray]);

	const handleClickMoreMenu = (
		event: React.MouseEvent<HTMLElement>,
		highlightedTextId: string,
	) => {
		event.preventDefault();
		setAnchorEl(event.currentTarget);
		const selectedText = highlightedTextArray.filter(
			(item) => item.id === highlightedTextId,
		)[0];
		setSelectedHighlightedText(selectedText);
	};

	const handleCloseMoreMenu = () => {
		setAnchorEl(null);
		setSelectedHighlightedText(null);
		setIsEditEnabled(false);
	};

	const handleEdit = () => {
		setIsEditEnabled(true);
		setAnchorEl(null);
	};

	const handleEndHighlight = () => {
		dispatch(setParserEndHighlightId(selectedHighlightedText));
		dispatch(setHighlightButtonText('End Highlight'));
		setAnchorEl(null);
	};

	const handleDelete = () => {
		const updatedValues = highlightedTextArray.filter(
			(item) => item.id !== selectedHighlightedText.id,
		);
		if (selectedHighlightedText.id === endHighlightId.id) {
			dispatch(setHighlightButtonText('Highlight'));
		}
		setHighlightedTextArray(updatedValues);
		dispatch(setParserHighlights(updatedValues));
		setAnchorEl(null);
	};

	const handleApprove = () => {
		setIsEditEnabled(false);
		setHighlightedTextArray((prevData) => {
			const items = prevData.map((res) => {
				if (res.id === selectedHighlightedText.id) {
					return {
						...res,
						content: {
							...res.content,
							text: selectedHighlightedText.text,
						},
					};
				}
				return res;
			});
			return items;
		});
	};

	const handleReject = () => {
		setIsEditEnabled(false);
		setSelectedHighlightedText((prevData: any) => {
			const selectedText = highlightedTextArray.filter(
				(item) => item.id === prevData.id,
			)[0];
			const updateItem = { ...prevData };
			updateItem.text = selectedText.content.text;
			return updateItem;
		});
	};

	const updateHash = (highlight: IHighlight) => {
		document.location.hash = `highlight-${highlight.id}`;
	};

	const filteredArray = highlightedTextArray.filter(
		(res) => res.comment.text !== 'not allowed',
	);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{filteredArray.length > 0 ? (
					filteredArray.map((highlightedText) => (
						<Paper
							key={`${highlightedText.id}`}
							onClick={() => updateHash(highlightedText)}
							className={styles['highlighted-text-paper']}
						>
							<Grid container spacing={1}>
								<Grid item xs={10} sm={10} md={10}>
									<Stack direction="column" spacing={2} padding={0.5}>
										{isEditEnabled &&
										selectedHighlightedText.id === highlightedText.id ? (
											<>
												<div>
													<TextFieldWithLabel
														inputProps={{
															'data-testid': 'hightlighted-text-edit',
															className: styles['highlighted-text-edit'],
														}}
														id="highlighted-text-area"
														defaultValue={selectedHighlightedText.content.text}
														onChange={(
															event: React.ChangeEvent<HTMLInputElement>,
														) => {
															setSelectedHighlightedText((prevData: any) => ({
																...prevData,
																text: event.target.value,
															}));
														}}
														multiLine
														showLabel={false}
														error={false}
														helperText=""
														className={styles['highlighted-text-area']}
													/>
												</div>
												<div>
													<IconButton onClick={handleApprove}>
														<CircleCheck
															data-testid="circle-check-icon"
															className={styles['circle-check-styles']}
														/>
													</IconButton>
													<IconButton onClick={handleReject}>
														<CircleDeleteCheck
															data-testid="circle-delete-check-icon"
															className={styles['circle-check-styles']}
														/>
													</IconButton>
												</div>
											</>
										) : (
											<Typography className={styles['highlighted-text']}>
												<Tooltip
													title={highlightedText.content.text}
													placement="bottom-start"
												>
													<span data-testid="highlighted-text">
														{highlightedText.content.text}
													</span>
												</Tooltip>
											</Typography>
										)}
									</Stack>
									<Stack direction="column" spacing={2} padding={0.5}>
										<Typography
											className={styles['highlighted-text-page']}
											data-testid="highlighted-text-page"
										>{`Page ${highlightedText.position.pageNumber}`}</Typography>
									</Stack>
								</Grid>
								<Grid
									item
									xs={2}
									sm={2}
									md={2}
									onClick={(event) =>
										handleClickMoreMenu(event, highlightedText.id)
									}
									className={styles['highlighted-text-more-menu']}
								>
									<MoreMenu data-testid="highlighted-text-more-menu" />
								</Grid>
								<Menu
									anchorEl={anchorEl}
									onClose={handleCloseMoreMenu}
									open={Boolean(anchorEl)}
									key={`${highlightedText.id}_menu`}
									slotProps={{
										paper: {
											style: {
												width: '14rem',
												padding: '0 8px',
											},
										},
									}}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'center',
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
								>
									<MenuItem value="edit" key="edit" onClick={handleEdit}>
										Edit
									</MenuItem>
									<MenuItem value="delete" key="delete" onClick={handleDelete}>
										Delete
									</MenuItem>
									<MenuItem
										value="highlight"
										key="highlight"
										disabled={
											selectedHighlightedText?.comment?.text?.length !== 0
										}
										onClick={handleEndHighlight}
									>
										End Highlight
									</MenuItem>
								</Menu>
							</Grid>
						</Paper>
					))
				) : (
					<div>
						<Stack direction="column" className={styles['no-highlight-icon']}>
							<FolderOpenAngledIcon data-testid="no-highlight-icon" />
						</Stack>
						<Stack
							direction="column"
							spacing={1}
							className={styles['no-highlight-main-text']}
						>
							Nothing here at the moment
						</Stack>
						<Stack
							direction="column"
							spacing={2}
							className={styles['no-highlight-sub-text']}
						>
							Start highlighting the sections you want to parse
						</Stack>
					</div>
				)}
			</Grid>
		</Grid>
	);
};

export default SelectSection;
